import React, {
    useMemo,
} from "react";

import {
    Badge,
    Button,
    Card,
    Col,
    Descriptions,
    Empty,
    Modal,
    Row,
    Space,
    Spin,
    Statistic,
    Tag,
    Typography,
} from "antd";

import {
    CheckCircleFilled,
    CheckCircleOutlined,
    DatabaseOutlined,
    FilePdfOutlined,
    FileSearchOutlined,
    SafetyCertificateOutlined,
} from "@ant-design/icons";

const {
    Text,
    Title,
} = Typography;

const getDisplayValue = (
    value,
    fallback = "—"
) => {
    if (
        value === null ||
        value === undefined ||
        value === ""
    ) {
        return fallback;
    }

    return value;
};

const getTopicCount = (
    period
) => {
    if (
        typeof period?.total_topics ===
        "number"
    ) {
        return period.total_topics;
    }

    if (
        Array.isArray(
            period?.topics
        )
    ) {
        return period.topics.length;
    }

    return 0;
};

const getPeriodCount = (
    data
) => {
    if (
        typeof data?.total_periods ===
        "number"
    ) {
        return data.total_periods;
    }

    if (
        typeof data?.periods ===
        "number"
    ) {
        return data.periods;
    }

    if (
        Array.isArray(
            data?.periods
        )
    ) {
        return data.periods.length;
    }

    if (
        Array.isArray(
            data?.data?.periods
        )
    ) {
        return data.data.periods.length;
    }

    return 0;
};

const getSubjects = (
    data
) => {
    if (
        Array.isArray(
            data?.subjects
        )
    ) {
        return data.subjects;
    }

    if (
        Array.isArray(
            data?.data?.subjects
        )
    ) {
        return data.data.subjects;
    }

    return [];
};

const getPeriods = (
    data
) => {
    if (
        Array.isArray(
            data?.periods
        )
    ) {
        return data.periods;
    }

    if (
        Array.isArray(
            data?.data?.periods
        )
    ) {
        return data.data.periods;
    }

    return [];
};

const getTopics = (
    data
) => {
    if (
        Array.isArray(
            data?.topics
        )
    ) {
        return data.topics;
    }

    if (
        Array.isArray(
            data?.data?.topics
        )
    ) {
        return data.data.topics;
    }

    const periods =
        getPeriods(data);

    return periods.flatMap(
        (period) =>
            Array.isArray(
                period?.topics
            )
                ? period.topics
                : []
    );
};

const StatCard = ({
    title,
    value,
    icon,
    color,
}) => {
    return (
        <Card
            size="small"
            style={{
                height: "100%",
                borderRadius: 10,
            }}
        >
            <Statistic
                title={
                    <Space size={6}>
                        {icon}
                        <span>
                            {title}
                        </span>
                    </Space>
                }
                value={value}
                valueStyle={{
                    color,
                    fontWeight: 700,
                }}
            />
        </Card>
    );
};

const SubjectCard = ({
    subject,
    index,
}) => {
    const name =
        typeof subject ===
        "string"
            ? subject
            : subject?.name ||
              subject?.subject_name ||
              `Subject ${index + 1}`;

    return (
        <Card
            size="small"
            style={{
                height: "100%",
                borderRadius: 10,
            }}
        >
            <Space
                align="start"
                style={{
                    width: "100%",
                }}
            >
                <Badge
                    count={
                        index + 1
                    }
                    style={{
                        backgroundColor:
                            "#f97316",
                    }}
                />

                <Space
                    direction="vertical"
                    size={0}
                >
                    <Text strong>
                        {name}
                    </Text>

                    {subject?.id && (
                        <Text
                            type="secondary"
                            style={{
                                fontSize: 12,
                            }}
                        >
                            ID:{" "}
                            {
                                subject.id
                            }
                        </Text>
                    )}
                </Space>
            </Space>
        </Card>
    );
};

const PeriodCard = ({
    period,
    index,
}) => {
    const semester =
        period?.semester_display ||
        period?.semester ||
        "—";

    const subject =
        period?.subject_name ||
        period?.subject?.name ||
        "—";

    const grade =
        period?.grade_name ||
        period?.grade_class?.name ||
        "—";

    const name =
        period?.name ||
        `Period ${index + 1}`;

    const topicCount =
        getTopicCount(
            period
        );

    return (
        <Card
            size="small"
            style={{
                height: "100%",
                borderRadius: 10,
            }}
        >
            <Space
                direction="vertical"
                size={8}
                style={{
                    width: "100%",
                }}
            >
                <Space
                    wrap
                    size={[6, 6]}
                >
                    <Tag color="blue">
                        {semester}
                    </Tag>

                    <Tag color="purple">
                        {subject}
                    </Tag>

                    <Tag color="gold">
                        {grade}
                    </Tag>
                </Space>

                <Text strong>
                    {name}
                </Text>

                <Text
                    type="secondary"
                >
                    {topicCount}{" "}
                    topic
                    {topicCount ===
                    1
                        ? ""
                        : "s"}
                </Text>
            </Space>
        </Card>
    );
};

const TopicCard = ({
    topic,
    index,
}) => {
    const name =
        topic?.name ||
        `Topic ${index + 1}`;

    const content =
        topic?.content ||
        topic?.learning_outcomes ||
        topic?.learning_objectives ||
        "";

    return (
        <Card
            size="small"
            style={{
                height: "100%",
                borderRadius: 10,
            }}
        >
            <Space
                direction="vertical"
                size={6}
                style={{
                    width: "100%",
                }}
            >
                <Space>
                    <Badge
                        count={
                            index + 1
                        }
                        style={{
                            backgroundColor:
                                "#f97316",
                        }}
                    />

                    <Text strong>
                        {name}
                    </Text>
                </Space>

                {content && (
                    <Text
                        type="secondary"
                        ellipsis={{
                            rows: 3,
                            tooltip:
                                content,
                        }}
                    >
                        {content}
                    </Text>
                )}

                <Space
                    wrap
                    size={[
                        4,
                        4,
                    ]}
                >
                    {topic
                        ?.learning_objectives && (
                        <Tag>
                            Learning
                            Objectives
                        </Tag>
                    )}

                    {topic
                        ?.learning_outcomes && (
                        <Tag>
                            Learning
                            Outcomes
                        </Tag>
                    )}

                    {topic
                        ?.competencies && (
                        <Tag>
                            Competencies
                        </Tag>
                    )}
                </Space>
            </Space>
        </Card>
    );
};

const CurriculumPreviewPopup = ({
    visible,
    open,
    loading = false,
    preview,
    source,
    imported = false,
    importLoading = false,
    onClose,
    onCancel,
    onImport,
    onConfirmImport,
}) => {
    const isVisible =
        visible ??
        open ??
        false;

    const handleClose =
        onClose ||
        onCancel ||
        (() => {});

    const handleImport =
        onImport ||
        onConfirmImport ||
        (() => {});

    const subjects =
        useMemo(
            () =>
                getSubjects(
                    preview
                ),
            [preview]
        );

    const periods =
        useMemo(
            () =>
                getPeriods(
                    preview
                ),
            [preview]
        );

    const topics =
        useMemo(
            () =>
                getTopics(
                    preview
                ),
            [preview]
        );

    const subjectCount =
        preview?.total_subjects ??
        preview?.subjects ??
        subjects.length;

    const gradeCount =
        preview?.total_grades ??
        preview?.grades ??
        (
            Array.isArray(
                preview?.data?.grades
            )
                ? preview.data.grades
                    .length
                : 0
        );

    const semesterCount =
        preview?.total_semesters ??
        preview?.semesters ??
        new Set(
            periods
                .map(
                    (item) =>
                        item?.semester
                )
                .filter(Boolean)
        ).size;

    const periodCount =
        preview?.total_periods ??
        getPeriodCount(
            preview
        );

    const topicCount =
        preview?.total_topics ??
        preview?.topics ??
        topics.length;

    const curriculumName =
        preview?.source
            ?.curriculum ||
        source?.curriculum_name ||
        source?.curriculum?.name ||
        "Liberia National Curriculum";

    const academicYear =
        preview?.source
            ?.academic_year_name ||
        source?.academic_year_name ||
        source?.academic_year?.name ||
        "—";

    const version =
        preview?.source
            ?.version ||
        source?.version ||
        preview?.data
            ?.version ||
        "—";

    const pageCount =
        preview?.pages ||
        preview?.total_pages ||
        preview?.data
            ?.pages ||
        "—";

    const fileName =
        preview?.source
            ?.file ||
        preview?.source
            ?.file_name ||
        source?.file_name ||
        source?.uploaded_file
            ?.split("/")
            .pop() ||
        "—";

    return (
        <Modal
            open={isVisible}
            visible={
                undefined
            }
            onCancel={
                handleClose
            }
            width={1100}
            centered
            destroyOnClose
            title={
                <Space>
                    <FileSearchOutlined
                        style={{
                            color:
                                "#f97316",
                        }}
                    />

                    <span>
                        Curriculum Preview
                    </span>
                </Space>
            }
            footer={
                <Space>
                    <Button
                        onClick={
                            handleClose
                        }
                    >
                        Close
                    </Button>

                    {!imported && (
                        <Button
                            type="primary"
                            icon={
                                <CheckCircleOutlined />
                            }
                            loading={
                                importLoading
                            }
                            disabled={
                                loading ||
                                !preview
                            }
                            onClick={
                                handleImport
                            }
                        >
                            Confirm & Import
                        </Button>
                    )}

                    {imported && (
                        <Button
                            type="primary"
                            icon={
                                <CheckCircleFilled />
                            }
                            onClick={
                                handleClose
                            }
                        >
                            Done
                        </Button>
                    )}
                </Space>
            }
        >
            {loading ? (
                <div
                    style={{
                        minHeight: 360,
                        display:
                            "flex",
                        alignItems:
                            "center",
                        justifyContent:
                            "center",
                    }}
                >
                    <Space
                        direction="vertical"
                        align="center"
                    >
                        <Spin
                            size="large"
                        />

                        <Text type="secondary">
                            Extracting curriculum content...
                        </Text>
                    </Space>
                </div>
            ) : !preview ? (
                <Empty
                    description="No curriculum preview is available."
                />
            ) : (
                <Space
                    direction="vertical"
                    size={20}
                    style={{
                        width: "100%",
                    }}
                >
                    <Card
                        size="small"
                        style={{
                            borderRadius: 10,
                        }}
                    >
                        <Descriptions
                            bordered
                            size="small"
                            column={{
                                xs: 1,
                                sm: 2,
                                md: 4,
                            }}
                        >
                            <Descriptions.Item label="Curriculum">
                                {
                                    curriculumName
                                }
                            </Descriptions.Item>

                            <Descriptions.Item label="Academic Year">
                                {
                                    academicYear
                                }
                            </Descriptions.Item>

                            <Descriptions.Item label="Version">
                                {
                                    getDisplayValue(
                                        version
                                    )
                                }
                            </Descriptions.Item>

                            <Descriptions.Item label="Pages">
                                {
                                    getDisplayValue(
                                        pageCount
                                    )
                                }
                            </Descriptions.Item>

                            <Descriptions.Item
                                label="Source"
                                span={2}
                            >
                                {
                                    fileName
                                }
                            </Descriptions.Item>

                            <Descriptions.Item label="Source Subject">
                                {
                                    preview?.source_subject ||
                                    preview?.data
                                        ?.source_subject ||
                                    "—"
                                }
                            </Descriptions.Item>

                            <Descriptions.Item label="Status">
                                {imported ? (
                                    <Tag color="green">
                                        <CheckCircleFilled />
                                        {" "}
                                        Imported
                                    </Tag>
                                ) : (
                                    <Tag color="blue">
                                        Ready for import
                                    </Tag>
                                )}
                            </Descriptions.Item>
                        </Descriptions>
                    </Card>

                    <Row
                        gutter={[
                            12,
                            12,
                        ]}
                    >
                        <Col
                            xs={24}
                            sm={8}
                        >
                            <StatCard
                                title="Subjects"
                                value={
                                    subjectCount
                                }
                                icon={
                                    <DatabaseOutlined />
                                }
                                color="#f97316"
                            />
                        </Col>

                        <Col
                            xs={24}
                            sm={8}
                        >
                            <StatCard
                                title="Periods"
                                value={
                                    periodCount
                                }
                                icon={
                                    <FileSearchOutlined />
                                }
                                color="#2563eb"
                            />
                        </Col>

                        <Col
                            xs={24}
                            sm={8}
                        >
                            <StatCard
                                title="Topics"
                                value={
                                    topicCount
                                }
                                icon={
                                    <SafetyCertificateOutlined />
                                }
                                color="#16a34a"
                            />
                        </Col>
                    </Row>

                    <Card
                        size="small"
                        title={
                            <Space>
                                <DatabaseOutlined />
                                <span>
                                    Subjects
                                </span>

                                <Badge
                                    count={
                                        subjects.length
                                    }
                                    showZero
                                />
                            </Space>
                        }
                        style={{
                            borderRadius: 10,
                        }}
                    >
                        {subjects.length ===
                        0 ? (
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description="No subjects detected."
                            />
                        ) : (
                            <Row
                                gutter={[
                                    12,
                                    12,
                                ]}
                            >
                                {subjects.map(
                                    (
                                        subject,
                                        index
                                    ) => (
                                        <Col
                                            key={
                                                subject?.id ||
                                                subject?.name ||
                                                index
                                            }
                                            xs={24}
                                            sm={12}
                                            md={8}
                                            lg={6}
                                        >
                                            <SubjectCard
                                                subject={
                                                    subject
                                                }
                                                index={
                                                    index
                                                }
                                            />
                                        </Col>
                                    )
                                )}
                            </Row>
                        )}
                    </Card>

                    <Card
                        size="small"
                        title={
                            <Space>
                                <FileSearchOutlined />
                                <span>
                                    Periods
                                </span>

                                <Badge
                                    count={
                                        periodCount
                                    }
                                    showZero
                                />
                            </Space>
                        }
                        style={{
                            borderRadius: 10,
                        }}
                    >
                        {periods.length ===
                        0 ? (
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description="No periods detected."
                            />
                        ) : (
                            <Row
                                gutter={[
                                    12,
                                    12,
                                ]}
                            >
                                {periods.map(
                                    (
                                        period,
                                        index
                                    ) => (
                                        <Col
                                            key={
                                                period?.id ||
                                                `${period?.name}-${period?.semester}-${index}`
                                            }
                                            xs={24}
                                            md={12}
                                            lg={8}
                                        >
                                            <PeriodCard
                                                period={
                                                    period
                                                }
                                                index={
                                                    index
                                                }
                                            />
                                        </Col>
                                    )
                                )}
                            </Row>
                        )}
                    </Card>

                    <Card
                        size="small"
                        title={
                            <Space>
                                <SafetyCertificateOutlined />
                                <span>
                                    Topics
                                </span>

                                <Badge
                                    count={
                                        topicCount
                                    }
                                    showZero
                                />
                            </Space>
                        }
                        style={{
                            borderRadius: 10,
                        }}
                    >
                        {topics.length ===
                        0 ? (
                            <Empty
                                image={
                                    Empty.PRESENTED_IMAGE_SIMPLE
                                }
                                description="No topics detected."
                            />
                        ) : (
                            <Row
                                gutter={[
                                    12,
                                    12,
                                ]}
                            >
                                {topics.map(
                                    (
                                        topic,
                                        index
                                    ) => (
                                        <Col
                                            key={
                                                topic?.id ||
                                                `${topic?.name}-${index}`
                                            }
                                            xs={24}
                                            md={12}
                                            lg={8}
                                        >
                                            <TopicCard
                                                topic={
                                                    topic
                                                }
                                                index={
                                                    index
                                                }
                                            />
                                        </Col>
                                    )
                                )}
                            </Row>
                        )}
                    </Card>

                    <Card
                        size="small"
                        style={{
                            borderRadius: 10,
                        }}
                    >
                        <Space
                            direction="vertical"
                            style={{
                                width: "100%",
                            }}
                        >
                            <Space>
                                <CheckCircleFilled
                                    style={{
                                        color:
                                            "#16a34a",
                                    }}
                                />

                                <Text strong>
                                    Extraction Status
                                </Text>
                            </Space>

                            <Space
                                wrap
                                size={[
                                    6,
                                    6,
                                ]}
                            >
                                <Tag
                                    color={
                                        preview?.has_subjects
                                            ? "green"
                                            : "default"
                                    }
                                >
                                    Subjects
                                </Tag>

                                <Tag
                                    color={
                                        preview?.has_grades
                                            ? "green"
                                            : "default"
                                    }
                                >
                                    Grades
                                </Tag>

                                <Tag
                                    color={
                                        preview?.has_semesters
                                            ? "green"
                                            : "default"
                                    }
                                >
                                    Semesters
                                </Tag>

                                <Tag
                                    color={
                                        preview?.has_periods
                                            ? "green"
                                            : "default"
                                    }
                                >
                                    Periods
                                </Tag>

                                <Tag
                                    color={
                                        preview?.has_topics
                                            ? "green"
                                            : "default"
                                    }
                                >
                                    Topics
                                </Tag>
                            </Space>
                        </Space>
                    </Card>
                </Space>
            )}
        </Modal>
    );
};

export default CurriculumPreviewPopup;